// PerformanceMetrics.jsx
import React from 'react';
import usePerformanceMetrics from './usePerformanceMetrics';
import { TrendingUpIcon, RefreshIcon, ErrorIcon, CheckCircleIcon, StorageIcon, CloudUploadIcon, WarningIcon } from './CustomIcons';
import './PerformanceMetrics.css'; // Import the CSS file

const PerformanceMetrics = () => {
  const {
    percentage,
    loading,
    refreshing,
    totalData,
    uploadedData,
    error,
    animatedPercentage,
    handleRefresh,
    getStatusColor,
    formatLastUpdated,
  } = usePerformanceMetrics();

  const getStatusIcon = () => {
    if (percentage >= 90) return <CheckCircleIcon style={{ color: '#4caf50' }} />;
    if (percentage >= 50) return <TrendingUpIcon style={{ color: '#2196f3' }} />;
    return <WarningIcon style={{ color: '#ff9800' }} />;
  };

  // Inline styles are kept for dynamic values and simplicity within the component,
  // but static styles are moved to CSS.

  if (loading) {
    return (
      <div className="performance-metrics-card">
        <div className="loading-pulse">
          <div className="header-section">
            <div className="title-section" style={{ opacity: 0.3 }}>
              <div className="loading-placeholder" style={{ width: '24px', height: '24px' }}></div>
              <div className="loading-placeholder" style={{ width: '200px', height: '24px' }}></div>
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <div className="loading-placeholder" style={{ width: '75%', height: '16px', marginBottom: '16px' }}></div>
            <div className="loading-placeholder" style={{ width: '100%', height: '32px' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="performance-metrics-card error">
        <div className="header-section">
          <div className="title-section">
            <ErrorIcon style={{ color: '#f44336' }} />
            <h3 className="header-title">Data Upload Coverage</h3>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`refresh-button error-state ${refreshing ? 'refreshing' : ''}`}
          >
            <RefreshIcon className="refresh-icon" />
            <span>Retry</span>
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#d32f2f' }}>
          <ErrorIcon />
          <span>Error: {error}</span>
        </div>
      </div>
    );
  }

  if (totalData === 0) {
    return (
      <div className="performance-metrics-card">
        <div className="header-section">
          <div className="title-section">
            <TrendingUpIcon style={{ color: '#544040' }} />
            <h3 className="header-title">Data Upload Coverage</h3>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`refresh-button ${refreshing ? 'refreshing' : ''}`}
          >
            <RefreshIcon className="refresh-icon" />
            <span>Refresh</span>
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <StorageIcon style={{ fontSize: '48px', color: '#ccc', marginBottom: '12px' }} />
          <p style={{ fontSize: '18px', color: '#666', margin: '0 0 4px 0' }}>No data available</p>
          <p style={{ fontSize: '14px', color: '#999', margin: 0 }}>Check back later or contact support</p>
        </div>
      </div>
    );
  }

  return (
    <div className="performance-metrics-card">
      {/* Header */}
      <div className="header-section">
        <div className="title-section">
          <div className="icon-container">
            <TrendingUpIcon style={{ color: '#1976d2' }} />
          </div>
          <div>
            <h3 className="header-title">Data Upload Coverage</h3>
            <p className="last-updated-text">
              Last updated {formatLastUpdated()}
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className={`refresh-button ${refreshing ? 'refreshing' : ''}`}
        >
          <RefreshIcon className="refresh-icon" />
          <span>{refreshing ? 'Updating...' : 'Refresh'}</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header">
            <StorageIcon style={{ fontSize: '16px', color: '#666' }} />
            <span className="stat-card-label">Total Products</span>
          </div>
          <p className="stat-card-value">
            {totalData.toLocaleString()}
          </p>
        </div>

        <div className="stat-card uploaded">
          <div className="stat-card-header">
            <CloudUploadIcon style={{ fontSize: '16px', color: '#1976d2' }} />
            <span className="stat-card-label">Uploaded</span>
          </div>
          <p className="stat-card-value">
            {uploadedData.toLocaleString()}
          </p>
        </div>

        <div className="stat-card coverage">
          <div className="stat-card-header">
            {getStatusIcon()}
            <span className="stat-card-label">Coverage</span>
          </div>
          <p className="stat-card-value" style={{ color: getStatusColor() }}>
            {animatedPercentage.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Progress Section */}
      <div className="progress-section">
        <div className="progress-header">
          <span className="progress-label">
            Progress: {uploadedData.toLocaleString()} / {totalData.toLocaleString()}
          </span>
          <span className="progress-percentage" style={{ color: getStatusColor() }}>
            {percentage.toFixed(1)}% <span className="total-percent-text">/ 100%</span>
          </span>
        </div>

        {/* Progress Bar */}
        <div className="progress-bar-container">
          <div className="progress-bar" style={{ background: `linear-gradient(90deg, ${getStatusColor()}, ${getStatusColor()}dd)`, width: `${Math.min(percentage, 100)}%` }} />
          <div className="goal-marker" />
        </div>

        {/* Scale */}
        <div className="progress-scale">
          <span>0%</span>
          <span className="goal-text">Goal: 100%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Status Message */}
      <div className="status-message">
        <div className="status-message-content">
          {getStatusIcon()}
          <span className="status-message-text">
            {percentage >= 95 ? "Excellent coverage! Almost complete." :
              percentage >= 80 ? "Good progress! Keep it up." :
              percentage >= 60 ? "Making steady progress." :
              percentage >= 40 ? "More uploads needed to reach the goal." :
              "Consider prioritizing data uploads."}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;